import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { ApiError, api, isRemoteSignOut } from "@/core/api/client";
import { hydrateAccountPreferences } from "@/core/state/preferences";
import { BootScreen } from "@/shared/boot/BootScreen";
import { useSession } from "@/core/state/session";

const SESSION_CHECK_MS = 3_000;

export function AuthGate() {
  const identity = useSession((state) => state.identity);
  const signedOutRemotely = useSession((state) => state.signedOutRemotely);
  const setIdentity = useSession((state) => state.setIdentity);
  const [loading, setLoading] = useState(!identity);
  const location = useLocation();

  useEffect(() => {
    if (identity) return;
    let cancelled = false;
    api.me()
      .then(async ({ identity: current }) => {
        if (cancelled) return;
        setIdentity(current);
        try { await hydrateAccountPreferences(current.tenantSlug, api.account); }
        catch (error) { console.error(error); }
      })
      .catch((error: unknown) => { if (!cancelled && !(error instanceof ApiError && error.status === 401)) console.error(error); })
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [identity, setIdentity]);

  useEffect(() => {
    if (!identity) return;
    let cancelled = false;
    const check = () => {
      void api.me().catch((error: unknown) => {
        if (cancelled) return;
        if (isRemoteSignOut(error, "/bff/auth/me")) {
          useSession.getState().endRemoteSession();
          return;
        }
        if (!(error instanceof ApiError && (error.status === 0 || error.status === 504))) console.error(error);
      });
    };
    check();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") check();
    }, SESSION_CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [identity, location.pathname, location.search]);

  if (loading) return <BootScreen />;
  if (!identity) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}`, reason: signedOutRemotely ? "session-ended" : undefined }} />;
  return <Outlet />;
}
